/** Readable names for the activity log's actions, shared by the First week and Usage pages. */
// Readable names for the actions; anything else shows its own name with spaces.
const ACTION_NAMES: { [event: string]: string } = {
  app_opened: 'Opened the app',
  signed_in: 'Signed in',
  account_activated: 'Set a password (activated)',
  tasks_added: 'Added tasks',
  task_completed: 'Completed a task',
  task_reopened: 'Reopened a task',
  task_deleted: 'Deleted a task',
  task_edited: 'Edited a task',
  tasks_estimated: 'Set estimates',
  lms_tasks_synced: 'Tasks arrived from a course platform',
  google_tasks_synced: 'Tasks arrived from Google Calendar',
  lms_connected: 'Connected a course platform',
  course_imported_syllabus: 'Syllabus imported into a course',
  course_imported_share: 'Copied a shared course',
  session_planned: 'Planned a study session',
  session_moved: 'Moved a study session',
  session_resized: 'Resized a study session',
  session_deleted: 'Deleted a study session',
  session_marked_used: 'Marked a session used',
  sessions_unplanned: 'Unplanned sessions',
  timer_started: 'Started the timer',
  timer_stopped: 'Ended a timer run',
  timer_cancelled: 'Reset the timer',
  timer_capped: 'Timer hit the 8-hour cap',
  courses_set_up: 'Set up courses',
  course_added: 'Added a course',
  course_imported: 'Imported a course (syllabus or shared)',
  activity_added: 'Added an activity',
  activities_set_up: 'Set up activities',
  term_set_up: 'Set up the term',
  awake_times_set: 'Set awake times',
  setup_step_done: 'Finished a setup step',
  next_term_created: 'Created the next term',
  event_added: 'Added a calendar event',
  event_edited: 'Edited a calendar event',
  event_deleted: 'Deleted a calendar event',
  autoplan_dialog_opened: 'Opened Auto-plan',
  autoplan_previewed: 'Saw an Auto-plan preview',
  autoplan_run: 'Ran Auto-plan',
  autoplan_feedback_sent: 'Sent Auto-plan feedback',
  syllabus_uploaded: 'Uploaded a syllabus',
  syllabus_imported: 'Syllabus imported',
  syllabus_import_failed: 'Syllabus import failed',
  ai_app_approved: 'Connected an AI app',
  ai_tool_used: 'Used an AI app (MCP)',
  assistant_used: 'Used the in-app assistant',
  google_calendar_connected: 'Connected Google Calendar',
  lms_unsynced: 'Disconnected a course platform',
  undo: 'Used Undo',
  undo_failed: 'Undo found nothing to undo',
  paywall_viewed: 'Saw the paywall',
  membership_viewed: 'Opened membership settings',
  billing_portal_opened: 'Opened billing',
  cancel_requested: 'Cancelled in the app',
  cushion_viewed: 'Viewed the Cushion',
  streak_opened: 'Opened the study streak',
  estimate_box_opened: 'Opened Estimate tasks',
  settings_changed: 'Changed settings',
  onboarding_step_done: 'Finished an onboarding step',
  active_2plus_days: 'Active on 2+ days',
  active_3plus_days: 'Active on 3+ days',
  active_2plus_paid_weeks: 'Active in 2+ of the first 4 paid weeks',
  active_3plus_paid_weeks: 'Active in 3+ of the first 4 paid weeks',
  active_4plus_paid_weeks: 'Active in all 4 first paid weeks',
  active_days: 'Days active',
  missed_sessions_removed: 'Missed sessions removed (Auto-plan)',
  reviews_added: 'Added review sessions',
  calendar_moved: 'Moved the calendar (next, previous, today)',
  task_details_opened: "Opened a task's details",
  course_details_opened: 'Opened Course details',
  assistant_opened: 'Opened the in-app assistant',
  share_calendar_opened: 'Opened Share calendar'
};

// Pages the web app reports by name (page_viewed_tasks) and calendar views (calendar_view_changed_month).
const PAGE_NAMES: { [page: string]: string } = {
  calendar: 'Calendar',
  tasks: 'Tasks',
  charts: 'Analytics',
  term: 'Term',
  awake_time: 'Awake times',
  study_time: 'Study time',
  gpa_calculator: 'GPA calculator',
  settings: 'Settings',
  notifications: 'Notifications',
  connect_ai_app: 'Connect an AI app'
};

// Calendar changes arrive per kind of event (event_added_class, event_deleted_study_time).
const CALENDAR_VERBS: { [verb: string]: string } = { added: 'Added', edited: 'Edited', deleted: 'Deleted' };

export const actionName = (event: string) => {
  if (ACTION_NAMES[event]) return ACTION_NAMES[event];
  const page = /^page_viewed_(.+)$/.exec(event);
  if (page) return `Visited the ${PAGE_NAMES[page[1]] || page[1].replace(/_/g, ' ')} page`;
  const view = /^calendar_view_changed_(.+)$/.exec(event);
  if (view) return `Switched the calendar to ${view[1].replace(/_/g, ' ')} view`;
  const calendar = /^event_(added|edited|deleted)_(.+)$/.exec(event);
  if (calendar) return `${CALENDAR_VERBS[calendar[1]]} calendar: ${calendar[2].replace(/_/g, ' ')}`;
  const words = event.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The Usage page's areas, in the order a student meets them (Petr, 2026-10-20: actions that go together sit together). */
export const ACTION_AREAS: { key: string; title: string }[] = [
  { key: 'around', title: 'Opening the app and getting around' },
  { key: 'setup', title: 'Setting up' },
  { key: 'courses', title: 'Courses and activities' },
  { key: 'connections', title: 'Connections and imports' },
  { key: 'tasks', title: 'Tasks' },
  { key: 'planning', title: 'Planning study time' },
  { key: 'studying', title: 'Studying: timer and used time' },
  { key: 'calendar', title: 'Calendar events' },
  { key: 'grades', title: 'Grades' },
  { key: 'ai', title: 'AI apps and the assistant' },
  { key: 'undo', title: 'Undo' },
  { key: 'settings', title: 'Settings and account' },
  { key: 'membership', title: 'Membership and billing' },
  { key: 'other', title: 'Other' }
];

// Actions placed by name; the rest go by how their name starts (AREA_PREFIXES), else Other.
const AREA_OF: { [event: string]: string } = {
  app_opened: 'around',
  signed_in: 'around',
  calendar_moved: 'around',
  share_calendar_opened: 'around',
  term_switched: 'around',
  account_activated: 'setup',
  term_set_up: 'setup',
  term_deleted: 'setup',
  next_term_created: 'setup',
  setup_step_done: 'setup',
  onboarding_step_done: 'setup',
  demo_completed: 'setup',
  awake_times_set: 'setup',
  courses_set_up: 'setup',
  activities_set_up: 'setup',
  default_activities_added: 'setup',
  courses_reordered: 'courses',
  course_imported: 'connections',
  course_imported_syllabus: 'connections',
  course_imported_share: 'connections',
  tasks_added: 'tasks',
  tasks_estimated: 'tasks',
  tasks_categorized: 'tasks',
  subtask_edited: 'tasks',
  estimate_box_opened: 'tasks',
  page_ranges_edited: 'tasks',
  session_marked_used: 'studying',
  streak_opened: 'studying',
  sessions_unplanned: 'planning',
  missed_sessions_removed: 'planning',
  reviews_added: 'planning',
  unplan_dialog_opened: 'planning',
  min_study_block_changed: 'planning',
  cushion_setting_changed: 'planning',
  cushion_viewed: 'planning',
  gpa_calculator_used: 'grades',
  undo: 'undo',
  undo_failed: 'undo',
  redo: 'undo',
  ai_change_undone: 'undo',
  settings_changed: 'settings',
  account_edited: 'settings',
  email_changed: 'settings',
  password_changed: 'settings',
  paywall_viewed: 'membership',
  membership_viewed: 'membership',
  billing_portal_opened: 'membership',
  cancel_requested: 'membership',
  cancel_flow_opened: 'membership',
  resubscribe_clicked: 'membership',
  checkout_started: 'membership',
  subscription_reactivated: 'membership',
  trial_day_added: 'membership',
  trial_reset: 'membership'
};

const AREA_PREFIXES: [string, string][] = [
  ['page_viewed_', 'around'],
  ['calendar_view_changed_', 'around'],
  ['course_', 'courses'],
  ['activit', 'courses'],
  ['teacher_', 'courses'],
  ['lms_', 'connections'],
  ['google_', 'connections'],
  ['syllabus_', 'connections'],
  ['task_', 'tasks'],
  ['reading_source_', 'tasks'],
  ['session_', 'planning'],
  ['autoplan_', 'planning'],
  ['timer_', 'studying'],
  ['event_', 'calendar'],
  ['grade', 'grades'],
  ['ai_', 'ai'],
  ['assistant_', 'ai'],
  ['notification', 'settings'],
  ['coach_access_', 'settings'],
  ['widget_', 'settings']
];

export const actionArea = (event: string) => {
  if (AREA_OF[event]) return AREA_OF[event];
  const prefix = AREA_PREFIXES.find(([start]) => event.startsWith(start));
  return prefix ? prefix[1] : 'other';
};
